import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import complianceReportsCustomizationService from '../../../services/complianceReportsCustomizationService';
import Papa from 'papaparse';

const AblePayrollImportWizard = ({ onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    payPeriodStart: '',
    payPeriodEnd: '',
    file: null,
    fileName: ''
  });
  const [importSession, setImportSession] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = [
    { number: 1, title: 'Select Pay Period', icon: 'Calendar' },
    { number: 2, title: 'Upload ABLE Payroll File', icon: 'Upload' },
    { number: 3, title: 'Validate Compliance', icon: 'CheckCircle' },
    { number: 4, title: 'Generate IRC Form', icon: 'FileText' }
  ];

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        file: file,
        fileName: file?.name
      }));
      setError('');
    }
  };

  const handleStep1Next = () => {
    if (!formData?.payPeriodStart || !formData?.payPeriodEnd) {
      setError('Please select pay period dates');
      return;
    }
    setError('');
    setCurrentStep(2);
  };

  const handleStep2Next = async () => {
    if (!formData?.file) {
      setError('Please upload ABLE Payroll file');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create import session
      const session = await complianceReportsCustomizationService?.createAbleImport({
        importSessionName: `ABLE Import - ${formData?.payPeriodStart} to ${formData?.payPeriodEnd}`,
        payPeriodStart: formData?.payPeriodStart,
        payPeriodEnd: formData?.payPeriodEnd,
        fileName: formData?.fileName,
        fileSizeBytes: formData?.file?.size,
        importedBy: 'system'
      });

      setImportSession(session);

      // Parse CSV file
      Papa?.parse(formData?.file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            // Update session status
            await complianceReportsCustomizationService?.updateAbleImportStatus(
              session?.id,
              'processing',
              { totalRecords: results?.data?.length }
            );

            // Process records
            let processedCount = 0;
            const validationResults = [];

            for (const row of results?.data) {
              const record = {
                importSessionId: session?.id,
                employeeNumber: row?.['Employee Number'] || row?.employee_number,
                employeeName: row?.['Employee Name'] || row?.employee_name,
                department: row?.['Department'] || row?.department,
                grossPay: parseFloat(row?.['Gross Pay'] || row?.gross_pay || 0),
                basePay: parseFloat(row?.['Base Pay'] || row?.base_pay || 0),
                overtimePay: parseFloat(row?.['Overtime Pay'] || row?.overtime_pay || 0),
                allowances: parseFloat(row?.['Allowances'] || row?.allowances || 0),
                deductions: parseFloat(row?.['Deductions'] || row?.deductions || 0),
                swtAmount: parseFloat(row?.['SWT Amount'] || row?.swt_amount || 0),
                nasfundEmployee: parseFloat(row?.['NASFUND Employee'] || row?.nasfund_employee || 0),
                nasfundEmployer: parseFloat(row?.['NASFUND Employer'] || row?.nasfund_employer || 0),
                netPay: parseFloat(row?.['Net Pay'] || row?.net_pay || 0),
                hoursWorked: parseFloat(row?.['Hours Worked'] || row?.hours_worked || 0),
                overtimeHours: parseFloat(row?.['Overtime Hours'] || row?.overtime_hours || 0),
                rawData: row
              };

              // Validate record
              const validation = await complianceReportsCustomizationService?.validateAblePayrollRecord(record);
              
              record.validationStatus = validation?.validation_status;
              record.validationMessages = validation?.validation_messages;

              // Create import record
              await complianceReportsCustomizationService?.createAbleImportRecord(record);
              
              processedCount++;
              validationResults?.push({
                employeeName: record?.employeeName,
                status: validation?.validation_status,
                messages: validation?.validation_messages
              });
            }

            // Update session as completed
            await complianceReportsCustomizationService?.updateAbleImportStatus(
              session?.id,
              'completed',
              { 
                processedRecords: processedCount,
                failedRecords: validationResults?.filter(r => r?.status === 'failed')?.length
              }
            );

            setValidationResults(validationResults);
            setCurrentStep(3);
          } catch (err) {
            setError(err?.message || 'Failed to process import records');
            await complianceReportsCustomizationService?.updateAbleImportStatus(
              session?.id,
              'failed',
              { validationErrors: { error: err?.message } }
            );
          }
        },
        error: (err) => {
          setError('Failed to parse CSV file: ' + err?.message);
        }
      });
    } catch (err) {
      setError(err?.message || 'Failed to create import session');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Next = () => {
    const failedCount = validationResults?.filter(r => r?.status === 'failed')?.length;
    if (failedCount > 0) {
      setError(`Cannot proceed: ${failedCount} records failed validation`);
      return;
    }
    setError('');
    setCurrentStep(4);
  };

  const handleStep4Complete = async () => {
    try {
      setLoading(true);
      setError('');

      // Calculate totals from validation results
      const records = await complianceReportsCustomizationService?.getAbleImportRecords(importSession?.id);
      
      const totalEmployees = records?.length;
      const totalTaxableIncome = records?.reduce((sum, r) => sum + (r?.grossPay || 0), 0);
      const totalSwtWithheld = records?.reduce((sum, r) => sum + (r?.swtAmount || 0), 0);

      // Create IRC SWT form session
      const ircSession = await complianceReportsCustomizationService?.createIrcSwtSession({
        sessionName: `IRC SWT Form - ${formData?.payPeriodStart} to ${formData?.payPeriodEnd}`,
        payPeriodStart: formData?.payPeriodStart,
        payPeriodEnd: formData?.payPeriodEnd,
        ableImportId: importSession?.id,
        generatedBy: 'system'
      });

      // Update IRC session with calculated values
      await complianceReportsCustomizationService?.updateIrcSwtSessionStatus(
        ircSession?.id,
        'generated',
        {
          totalEmployees,
          totalTaxableIncome,
          totalSwtWithheld
        }
      );

      onComplete?.({
        importSession,
        ircSession,
        validationResults
      });
    } catch (err) {
      setError(err?.message || 'Failed to generate IRC form');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select the pay period for the ABLE Payroll data you want to import.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Pay Period Start
                </label>
                <Input
                  type="date"
                  value={formData?.payPeriodStart}
                  onChange={(e) => setFormData({ ...formData, payPeriodStart: e?.target?.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Pay Period End
                </label>
                <Input
                  type="date"
                  value={formData?.payPeriodEnd}
                  onChange={(e) => setFormData({ ...formData, payPeriodEnd: e?.target?.value })}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload the ABLE Payroll export file (CSV or Excel format).
            </p>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-sm font-medium text-foreground mb-1">
                  {formData?.fileName || 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-muted-foreground">
                  CSV or Excel file (max 10MB)
                </p>
              </label>
            </div>
            {formData?.fileName && (
              <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md flex items-center gap-2">
                <Icon name="CheckCircle" size={18} />
                <span className="text-sm">File selected: {formData?.fileName}</span>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Compliance validation results for imported payroll data.
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {validationResults?.map((result, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-md border ${
                    result?.status === 'passed' ?'bg-success/10 border-success'
                      : result?.status === 'warning' ?'bg-warning/10 border-warning' :'bg-error/10 border-error'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <Icon
                      name={result?.status === 'passed' ? 'CheckCircle' : result?.status === 'warning' ? 'AlertTriangle' : 'XCircle'}
                      size={18}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{result?.employeeName}</p>
                      {result?.messages?.length > 0 && (
                        <ul className="text-xs mt-1 space-y-1">
                          {result?.messages?.map((msg, i) => (
                            <li key={i}>• {msg}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-card border border-border rounded-md p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-success">
                    {validationResults?.filter(r => r?.status === 'passed')?.length}
                  </p>
                  <p className="text-xs text-muted-foreground">Passed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-warning">
                    {validationResults?.filter(r => r?.status === 'warning')?.length}
                  </p>
                  <p className="text-xs text-muted-foreground">Warnings</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-error">
                    {validationResults?.filter(r => r?.status === 'failed')?.length}
                  </p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Generate IRC SWT form for lodgment based on validated payroll data.
            </p>
            <div className="bg-card border border-border rounded-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pay Period:</span>
                <span className="text-sm font-medium text-foreground">
                  {formData?.payPeriodStart} to {formData?.payPeriodEnd}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Employees:</span>
                <span className="text-sm font-medium text-foreground">
                  {validationResults?.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Validation Status:</span>
                <span className="text-sm font-medium text-success">
                  {validationResults?.filter(r => r?.status === 'passed')?.length} / {validationResults?.length} Passed
                </span>
              </div>
            </div>
            <div className="bg-primary/10 border border-primary text-primary px-4 py-3 rounded-md flex items-start gap-2">
              <Icon name="Info" size={18} className="mt-0.5" />
              <div className="text-sm">
                <p className="font-medium mb-1">Ready for IRC Submission</p>
                <p className="text-xs opacity-90">
                  The IRC SWT form will be generated with all validated payroll data. You can submit it to the IRC portal after generation.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full mx-4">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground">
          ABLE Payroll Import Wizard
        </h2>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
          <Icon name="X" size={20} />
        </button>
      </div>
      {/* Step Indicator */}
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          {steps?.map((step, index) => (
            <React.Fragment key={step?.number}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentStep >= step?.number
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step?.number ? (
                    <Icon name="Check" size={20} />
                  ) : (
                    <Icon name={step?.icon} size={20} />
                  )}
                </div>
                <p className="text-xs text-center mt-2 max-w-[100px]">{step?.title}</p>
              </div>
              {index < steps?.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${
                    currentStep > step?.number ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Step Content */}
      <div className="p-6">
        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2 mb-4">
            <Icon name="AlertCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {renderStepContent()}
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between p-6 border-t border-border">
        <Button
          variant="outline"
          onClick={() => {
            if (currentStep === 1) {
              onCancel?.();
            } else {
              setCurrentStep(currentStep - 1);
              setError('');
            }
          }}
          disabled={loading}
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>

        <Button
          variant="primary"
          onClick={() => {
            if (currentStep === 1) handleStep1Next();
            else if (currentStep === 2) handleStep2Next();
            else if (currentStep === 3) handleStep3Next();
            else if (currentStep === 4) handleStep4Complete();
          }}
          loading={loading}
          disabled={loading}
        >
          {currentStep === 4 ? 'Generate IRC Form' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default AblePayrollImportWizard;